import React, { useState } from "react";
import { useNavigate, Link } from 'react-router-dom';
import { EnvelopeIcon, ArrowLeftIcon } from "@heroicons/react/24/outline";
import config from "../config";

const baseUrl = config.apiBaseUrl;

const Forgotten = () => {
    const [username, setUsername] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const navigate = useNavigate();

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        const formData = {
            Email: username
        };

        try {
            const response = await fetch(`${baseUrl}/api/users/reset`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(formData)
            });

            if (response.ok) {
                const data = await response.json();
                console.log('Success:', data);
                setSuccess(true);
                setTimeout(() => navigate('/login'), 3000);
            } else {
                throw new Error('Reset failed');
            }
        } catch (error) {
            console.error('Error:', error);
            setError('Failed to send reset email. Please check your email address and try again.');
        } finally {
            setLoading(false);
        }
    };

    if (success) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center p-4">
                <div className="w-full max-w-md">
                    <div className="backdrop-blur-sm bg-gray-900/20 border border-gray-700/30 rounded-xl p-8 text-center">
                        {/* Success Icon */}
                        <div className="flex justify-center mb-6">
                            <div className="w-16 h-16 bg-emerald-600/20 rounded-full flex items-center justify-center">
                                <EnvelopeIcon className="h-10 w-10 text-emerald-400" />
                            </div>
                        </div>

                        <h1 className="text-2xl font-bold text-white mb-4">
                            Email Sent!
                        </h1>

                        <p className="text-gray-300 text-base mb-6 leading-relaxed">
                            We've sent a password reset link to your email address. Please check your inbox and follow the instructions.
                        </p>

                        <p className="text-sm text-gray-400">
                            Redirecting to login in a few seconds...
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center p-4">
            <div className="w-full max-w-md">
                <div className="backdrop-blur-sm bg-gray-900/20 border border-gray-700/30 rounded-xl p-8">
                    {/* Back Link */}
                    <Link 
                        to="/login"
                        className="inline-flex items-center text-gray-400 hover:text-emerald-400 transition-colors duration-200 mb-6"
                    >
                        <ArrowLeftIcon className="h-4 w-4 mr-2" />
                        Back to login
                    </Link>

                    {/* Header */}
                    <div className="text-center mb-8">
                        <h1 className="text-2xl sm:text-3xl font-bold text-white mb-2">
                            Forgotten Password
                        </h1>
                        <p className="text-gray-300 text-sm sm:text-base">
                            Enter your email address and we'll send you a reset link
                        </p>
                    </div>

                    {/* Form */}
                    <form onSubmit={handleSubmit} className="space-y-6">
                        {/* Error Message */}
                        {error && (
                            <div className="bg-red-900/20 border border-red-800/30 rounded-lg p-4">
                                <p className="text-red-300 text-sm">{error}</p>
                            </div>
                        )}

                        {/* Email Input */}
                        <div>
                            <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                                Email Address
                            </label>
                            <div className="relative">
                                <input
                                    id="email"
                                    type="email"
                                    value={username}
                                    onChange={(e) => setUsername(e.target.value)}
                                    required
                                    className="w-full px-4 py-3 pl-12 bg-gray-800/50 border border-gray-600/50 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-400 transition-colors"
                                    placeholder="Enter your email address"
                                />
                                <EnvelopeIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                            </div>
                        </div>

                        {/* Submit Button */}
                        <button
                            type="submit"
                            disabled={loading}
                            className="w-full flex items-center justify-center px-6 py-3 bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-700 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-emerald-500/50"
                        >
                            {loading ? (
                                <>
                                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                                    Sending...
                                </>
                            ) : (
                                <>
                                    <EnvelopeIcon className="h-5 w-5 mr-2" />
                                    Send Reset Link
                                </>
                            )}
                        </button>
                    </form>

                    {/* Footer Links */}
                    <div className="mt-6 pt-6 border-t border-gray-700/50 text-center">
                        <p className="text-sm text-gray-400">
                            Remember your password?{' '}
                            <Link 
                                to="/login" 
                                className="text-emerald-400 hover:text-emerald-300 transition-colors duration-200"
                            >
                                Sign in here
                            </Link>
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default Forgotten;
